import { useState } from "react";
import { Play, Pause, SkipBack, SkipForward, Headphones, ExternalLink } from "lucide-react";

/**
 * RED FLAG Podcast Embed.
 *
 * When `spotify_embed_id` / `apple_embed_id` are set on the episode, the real
 * platform iframe is rendered. Otherwise a visual mock is shown so the layout
 * stays intact before feeds are live.
 *
 * Accepted formats for the IDs (the component auto-detects):
 *   Spotify   – full URL ("https://open.spotify.com/embed/episode/XYZ")
 *               or bare episode ID ("3T7B9rW6m...") — assumed to be an
 *               episode unless prefixed with "show/" / "playlist/".
 *   Apple     – full embed URL ("https://embed.podcasts.apple.com/...")
 *               — copy directly from Apple's "Embed" button.
 */
export default function PodcastEmbed({ episode }) {
  const [tab, setTab] = useState("spotify");
  const [playing, setPlaying] = useState(false);
  const content = episode.de || {};
  const title = content.title || "RED FLAG";
  const sub = `RED FLAG · EP ${episode.number}`;

  const spotifyUrl = buildSpotifyUrl(episode.spotify_embed_id);
  const appleUrl = buildAppleUrl(episode.apple_embed_id);

  return (
    <div
      data-testid="podcast-embed"
      className="border border-white/10 bg-[#0F0F0F] overflow-hidden"
    >
      <div className="flex border-b border-white/10">
        <button
          data-testid="embed-tab-spotify"
          onClick={() => setTab("spotify")}
          className={`flex-1 px-4 py-2.5 text-[11px] font-mono-plex uppercase tracking-widest transition-colors ${
            tab === "spotify"
              ? "text-[#1ED760] border-b-2 border-[#1ED760] bg-[#0A0A0A]"
              : "text-white/55 hover:text-white"
          }`}
        >
          Spotify
        </button>
        <button
          data-testid="embed-tab-apple"
          onClick={() => setTab("apple")}
          className={`flex-1 px-4 py-2.5 text-[11px] font-mono-plex uppercase tracking-widest transition-colors ${
            tab === "apple"
              ? "text-[#FA243C] border-b-2 border-[#FA243C] bg-[#0A0A0A]"
              : "text-white/55 hover:text-white"
          }`}
        >
          Apple Podcasts
        </button>
      </div>

      {tab === "spotify" ? (
        spotifyUrl ? (
          <iframe
            data-testid="spotify-iframe"
            title="Spotify Embed"
            src={spotifyUrl}
            width="100%"
            height="232"
            frameBorder="0"
            allow="autoplay; clipboard-write; encrypted-media; picture-in-picture"
            loading="lazy"
            className="bg-black"
          />
        ) : (
          <SpotifyMock
            episode={episode}
            title={title}
            sub={sub}
            playing={playing}
            setPlaying={setPlaying}
          />
        )
      ) : appleUrl ? (
        <iframe
          data-testid="apple-iframe"
          title="Apple Podcasts Embed"
          src={appleUrl}
          width="100%"
          height="175"
          frameBorder="0"
          allow="autoplay *; encrypted-media *; clipboard-write"
          sandbox="allow-forms allow-popups allow-same-origin allow-scripts allow-top-navigation-by-user-activation"
          loading="lazy"
          className="bg-black"
        />
      ) : (
        <AppleMock
          episode={episode}
          title={title}
          sub={sub}
          playing={playing}
          setPlaying={setPlaying}
        />
      )}

      <div className="px-4 py-3 border-t border-white/10 flex items-center justify-between text-[10px] font-mono-plex uppercase tracking-widest text-white/40">
        <span>
          {tab === "spotify"
            ? spotifyUrl
              ? "live embed"
              : "demo embed"
            : appleUrl
              ? "live embed"
              : "demo embed"}
        </span>
        <a
          href={tab === "spotify" ? spotifyUrl || "#" : appleUrl || "#"}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1.5 hover:text-redflag transition-colors"
        >
          <ExternalLink size={11} /> Open externally
        </a>
      </div>
    </div>
  );
}

// --- URL builders ---
function buildSpotifyUrl(value) {
  if (!value) return null;
  const v = String(value).trim();
  if (!v) return null;
  if (v.startsWith("http")) {
    // Accept full URL — auto-convert /show/ID or /episode/ID to /embed/ variant
    return v.replace("open.spotify.com/", "open.spotify.com/embed/").replace(
      "/embed/embed/",
      "/embed/"
    );
  }
  // Bare ID — assume episode
  return `https://open.spotify.com/embed/episode/${encodeURIComponent(v)}`;
}

function buildAppleUrl(value) {
  if (!value) return null;
  const v = String(value).trim();
  if (!v) return null;
  if (v.startsWith("http")) return v;
  // Allow short form like "id1234567890?i=1000123456789" — country defaults to ch
  return `https://embed.podcasts.apple.com/ch/podcast/${v}`;
}

function PlayerControls({ playing, setPlaying, accentColor }) {
  return (
    <div className="flex items-center gap-4">
      <button className="text-white/70 hover:text-white" aria-label="Previous">
        <SkipBack size={18} />
      </button>
      <button
        data-testid="embed-play"
        onClick={() => setPlaying((p) => !p)}
        className="h-12 w-12 rounded-full flex items-center justify-center text-black hover:scale-105 transition-transform"
        style={{ background: accentColor }}
        aria-label={playing ? "Pause" : "Play"}
      >
        {playing ? (
          <Pause size={18} fill="currentColor" />
        ) : (
          <Play size={18} fill="currentColor" className="ml-0.5" />
        )}
      </button>
      <button className="text-white/70 hover:text-white" aria-label="Next">
        <SkipForward size={18} />
      </button>
    </div>
  );
}

function ProgressBar({ accent, progress = 12 }) {
  return (
    <div className="mt-4">
      <div className="h-1 bg-white/12 rounded-full overflow-hidden">
        <div
          className="h-full rounded-full"
          style={{ width: `${progress}%`, background: accent }}
        />
      </div>
      <div className="flex justify-between mt-1.5 font-mono-plex text-[10px] text-white/50">
        <span>5:48</span>
        <span>-42:12</span>
      </div>
    </div>
  );
}

function SpotifyMock({ episode, title, sub, playing, setPlaying }) {
  return (
    <div data-testid="spotify-embed" className="p-5 bg-[#0F0F0F]">
      <div className="flex gap-4">
        <div className="h-24 w-24 shrink-0 overflow-hidden rounded-md">
          <img src={episode.cover} alt={title} className="w-full h-full object-cover" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-mono-plex text-[10px] uppercase tracking-widest text-[#1ED760]">
            Podcast · Episode
          </div>
          <div className="mt-1 font-medium text-white line-clamp-2 leading-tight">{title}</div>
          <div className="mt-1 text-xs text-white/55">{sub}</div>
        </div>
      </div>
      <ProgressBar accent="#1ED760" />
      <div className="mt-4 flex items-center justify-between">
        <PlayerControls playing={playing} setPlaying={setPlaying} accentColor="#1ED760" />
        <div className="font-mono-plex text-[10px] uppercase tracking-widest text-white/40">
          {episode.duration}
        </div>
      </div>
    </div>
  );
}

function AppleMock({ episode, title, sub, playing, setPlaying }) {
  return (
    <div data-testid="apple-embed" className="p-5 bg-[#0F0F0F]">
      <div className="flex gap-4 items-start">
        <div className="h-20 w-20 shrink-0 overflow-hidden rounded">
          <img src={episode.cover} alt={title} className="w-full h-full object-cover" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-mono-plex text-[10px] uppercase tracking-widest text-[#FA243C]">
            Apple Podcasts · {episode.category}
          </div>
          <div className="mt-1 font-medium text-white line-clamp-2 leading-tight">{title}</div>
          <div className="mt-1 text-xs text-white/55">{sub}</div>
        </div>
        <button
          onClick={() => setPlaying((p) => !p)}
          data-testid="embed-play"
          className="h-10 w-10 rounded-full bg-[#FA243C] text-white flex items-center justify-center shrink-0"
          aria-label={playing ? "Pause" : "Play"}
        >
          {playing ? (
            <Pause size={16} fill="currentColor" />
          ) : (
            <Play size={16} fill="currentColor" className="ml-0.5" />
          )}
        </button>
      </div>
      <ProgressBar accent="#FA243C" progress={8} />
      <div className="mt-3 flex items-center justify-between font-mono-plex text-[10px] uppercase tracking-widest text-white/45">
        <span>Released · {episode.released_iso}</span>
        <span>{episode.duration}</span>
      </div>
    </div>
  );
}
